import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import Axios from "axios";
import { URL_LIST_ALL } from "../configs/api";
import ModalSearchMulti from "../component/ModalSearchMulti";
import SearchBoxModal from "../component/SearchBoxModal";
import Spinner from "../component/Spinner";
import PreviewFile from "./PreviewFile";
import {
    isCustomTemplateParam,
    normalizeParamTitle,
    parseContractParams,
    toPlaceholder,
} from "./contractParams";

/**
 * Modal tạo hợp đồng cho NHIỀU nhân sự cùng lúc từ 1 mẫu.
 *
 * Mỗi nhân sự = 1 bản ghi hopdong riêng (POST tuần tự), vì backend merge file Word
 * theo từng user (hook add/hopdong_before.php). Không có API bulk nên gọi lần lượt
 * để mỗi hợp đồng có file_url riêng.
 */
export default function ModalThemNhieuHopDong({ show, onHide, onDone }) {
    const { user } = useSelector((state) => state.auth);

    const [title, setTitle] = useState("");
    const [typeId, setTypeId] = useState("");
    const [userIds, setUserIds] = useState([]);
    const [detailMau, setDetailMau] = useState(null);
    const [listMauParam, setListMauParam] = useState([]);
    // Danh sách tham số của mẫu chỉ để tham khảo => mặc định thu gọn.
    const [showMauParam, setShowMauParam] = useState(false);
    // Danh sách key param tùy biến của mẫu (chung cho mọi nhân sự): [{key, label}]
    const [customKeys, setCustomKeys] = useState([]);
    // Giá trị nhập RIÊNG theo từng nhân sự: {userId: {key: value}}
    const [paramsByUser, setParamsByUser] = useState({});
    // Giá trị điền nhanh áp cho tất cả: {key: value}
    const [fillAll, setFillAll] = useState({});
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // Kết quả từng nhân sự sau khi chạy: {user_id, label, status, message, id}
    const [results, setResults] = useState([]);
    const [done, setDone] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    // Nhãn nhân sự lấy từ ModalSearchMulti để hiển thị trong bảng kết quả.
    const [userLabels, setUserLabels] = useState({});
    // Khoá so sánh theo nội dung danh sách id đã chọn (mảng đổi tham chiếu mỗi lần tick).
    const userIdsKey = JSON.stringify(userIds);

    const loadMauHD = useCallback((typeid) => {
        Axios.get(URL_LIST_ALL + "hopdong_type/" + typeid, user.config)
            .then((res) => {
                if (res.data.status === "success") {
                    const templateParams = parseContractParams(res.data.data.params);
                    setListMauParam(templateParams);
                    setDetailMau(res.data.data);
                    // Chỉ param tùy biến mới cần nhập tay; param hệ thống backend tự merge.
                    const customs = templateParams
                        .filter(isCustomTemplateParam)
                        .map((item) => ({
                            key: toPlaceholder(item.title),
                            label: item.label || item.title || "",
                        }))
                        .filter((item) => normalizeParamTitle(item.key) !== "");
                    setCustomKeys(customs);
                    // Đổi mẫu => bộ param khác hẳn, bỏ giá trị đã nhập của mẫu cũ.
                    setParamsByUser({});
                    setFillAll({});
                } else console.log(res.data.message);
            })
            .catch((err) => console.log(err));
    }, [user.config]);

    const resetAll = () => {
        setTitle("");
        setTypeId("");
        setUserIds([]);
        setDetailMau(null);
        setListMauParam([]);
        setShowMauParam(false);
        setCustomKeys([]);
        setParamsByUser({});
        setFillAll({});
        setResults([]);
        setDone(false);
        setProgress({ current: 0, total: 0 });
        setErrors({});
        setUserLabels({});
    };

    const handleClose = () => {
        // Nếu đã tạo được hợp đồng thì báo cho list cha reload.
        if (results.some((r) => r.status === "success") && onDone) onDone();
        resetAll();
        onHide();
    };

    const validate = () => {
        const err = {};
        if (!title || title.trim() === "") err.title = "Vui lòng nhập tên văn bản hợp đồng";
        // Chừa 80 ký tự cho hậu tố " + họ tên + mã NV" ghép thêm khi tạo (giới hạn cột title là 200).
        else if (title.trim().length > 120) err.title = "Tiền tố không quá 120 ký tự (còn lại dành cho họ tên và mã nhân viên)";
        if (!typeId) err.type_id = "Vui lòng chọn mẫu hợp đồng";
        if (!userIds || userIds.length === 0) err.user_id = "Vui lòng chọn ít nhất 1 nhân sự";
        setErrors(err);
        return Object.keys(err).length === 0;
    };

    // Sửa 1 ô của 1 nhân sự cụ thể.
    const editParam = (uid, key, value) => {
        setParamsByUser((current) => ({
            ...current,
            [uid]: { ...(current[uid] || {}), [key]: value },
        }));
    };

    // Điền 1 giá trị cho TẤT CẢ nhân sự đang chọn (ghi đè ô cùng key của mọi người).
    const applyFillAll = (key) => {
        const value = fillAll[key] || "";
        setParamsByUser((current) => {
            const next = { ...current };
            userIds.forEach((uid) => {
                next[uid] = { ...(next[uid] || {}), [key]: value };
            });
            return next;
        });
    };

    // Copy toàn bộ ô của người đầu tiên xuống những người còn lại.
    const copyFirstToAll = () => {
        if (userIds.length < 2) return;
        const source = paramsByUser[userIds[0]] || {};
        setParamsByUser((current) => {
            const next = { ...current };
            userIds.slice(1).forEach((uid) => {
                next[uid] = { ...source };
            });
            return next;
        });
    };

    /**
     * Lấy họ tên + mã nhân viên cho các id đã chọn, để ghép vào tên hợp đồng.
     *
     * Hook api/more/users.php xử lý list_value, GHI ĐÈ asselect thành
     * ["users.id as value", "users.fullname as label"] khi chuỗi asselect có 'id',
     * và luôn thêm 'users.code as code' => đọc tên từ item.label, mã từ item.code.
     *
     * Trả về map {id: {name, code}}; id nào không lấy được thì không có key.
     */
    const fetchUserInfos = useCallback(async (ids) => {
        const map = {};
        try {
            const config = {
                headers: {
                    Authorization: "Bearer " + user.access_token,
                    asselect: JSON.stringify(["users.id as value", "users.fullname as label"]),
                },
            };
            const res = await Axios.get(
                URL_LIST_ALL + "users?list_value=" + encodeURIComponent(JSON.stringify(ids)) +
                "&limit=" + Math.max(ids.length, 10),
                config
            );
            if (res.data && res.data.status === "success" && Array.isArray(res.data.data)) {
                res.data.data.forEach((item) => {
                    if (item.value) map[item.value] = { name: item.label || "", code: item.code || "" };
                });
            }
        } catch (err) {
            console.log(err);
        }
        return map;
    }, [user.access_token]);

    // Bổ sung nhãn cho id chưa biết tên: onDataList của ModalSearchMulti chỉ đưa được các item
    // đang hiển thị, nên chọn qua nhiều lượt tìm kiếm sẽ có người thiếu nhãn => bảng hiện id trần.
    useEffect(() => {
        const missing = userIds.filter((uid) => !userLabels[uid]);
        if (missing.length === 0) return;
        let cancelled = false;
        fetchUserInfos(missing).then((infoMap) => {
            if (cancelled) return;
            setUserLabels((current) => {
                const next = { ...current };
                Object.keys(infoMap).forEach((uid) => {
                    const info = infoMap[uid];
                    next[uid] = info.code ? info.name + " (" + info.code + ")" : info.name;
                });
                return next;
            });
        });
        return () => { cancelled = true; };
        // Khoá theo NỘI DUNG userIds: ModalSearchMulti trả mảng mới mỗi lần tick, so theo
        // tham chiếu sẽ bắn request thừa. userLabels cố ý không nằm trong deps: effect tự
        // ghi vào nó, thêm vào sẽ tạo vòng lặp.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userIdsKey, fetchUserInfos]);

    // Tên hợp đồng = tiền tố + họ tên + mã NV. Phần nào thiếu thì bỏ qua, không chèn khoảng trắng thừa.
    const buildContractTitle = (prefix, info, fallbackLabel) => {
        const name = (info && info.name) || fallbackLabel || "";
        const code = (info && info.code) || "";
        return [prefix.trim(), name, code].filter((part) => part && String(part).trim() !== "").join(" ");
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setLoading(true);
        setDone(false);
        setResults([]);
        setProgress({ current: 0, total: userIds.length });

        // Tên hợp đồng = tiền tố chung + họ tên + mã NV của từng người.
        const infoMap = await fetchUserInfos(userIds);

        const localResults = [];
        // Gọi tuần tự (không Promise.all): mỗi request backend phải tải file mẫu về,
        // merge rồi upload FTP — bắn song song hàng chục request dễ làm quá tải/timeout.
        for (let i = 0; i < userIds.length; i++) {
            const uid = userIds[i];
            // Nhãn từ ModalSearchMulti làm dự phòng nếu request lấy thông tin lỗi.
            const contractTitle = buildContractTitle(title, infoMap[uid], userLabels[uid]);
            // Giá trị param tùy biến RIÊNG của nhân sự này; ô bỏ trống gửi chuỗi rỗng
            // để backend thay placeholder bằng rỗng thay vì để nguyên "{key}" trong file.
            const userParams = customKeys.map((c) => ({
                key: c.key,
                value: (paramsByUser[uid] || {})[c.key] || "",
            }));
            const values = {
                title: contractTitle,
                type_id: typeId,
                user_id: uid,
                params: JSON.stringify(userParams),
                author_id: user.data.id,
                status: 0,
            };

            try {
                const res = await Axios.post(URL_LIST_ALL + "hopdong", values, user.config);
                localResults.push({
                    user_id: uid,
                    label: userLabels[uid] || uid,
                    contract_title: contractTitle,
                    status: res.data.status === "success" ? "success" : "error",
                    message: res.data.status === "success" ? "Đã tạo" : (res.data.message || "Lỗi không rõ"),
                    id: res.data.id,
                });
            } catch (err) {
                localResults.push({
                    user_id: uid,
                    label: userLabels[uid] || uid,
                    contract_title: contractTitle,
                    status: "error",
                    message: err?.response?.data?.message || err.message || "Lỗi kết nối",
                });
            }
            setProgress({ current: i + 1, total: userIds.length });
            setResults(localResults.slice());
        }

        setLoading(false);
        setDone(true);
    };

    const successCount = results.filter((r) => r.status === "success").length;
    const errorCount = results.filter((r) => r.status === "error").length;

    return (
        <Modal show={show} onHide={handleClose} animation={true} size="xl" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Thêm nhiều hợp đồng</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* BƯỚC 2: kết quả sau khi tạo */}
                {done ? (
                    <>
                        <div className={"alert " + (errorCount > 0 ? "alert-warning" : "alert-success")}>
                            Đã tạo <strong>{successCount}</strong>/{results.length} hợp đồng
                            {errorCount > 0 && <> — <strong>{errorCount}</strong> lỗi</>}
                        </div>
                        <table className="table table-bordered table-sm">
                            <thead>
                                <tr>
                                    <th style={{ width: 50 }}>#</th>
                                    <th>Nhân sự</th>
                                    <th>Tên hợp đồng</th>
                                    <th style={{ width: 120 }}>Trạng thái</th>
                                    <th>Ghi chú</th>
                                    <th style={{ width: 110 }}>Hợp đồng</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((r, i) => (
                                    <tr key={r.user_id} className={r.status === "error" ? "table-danger" : ""}>
                                        <td>{i + 1}</td>
                                        <td>{r.label}</td>
                                        <td>{r.contract_title}</td>
                                        <td>
                                            {r.status === "success"
                                                ? <span className="text-success">Thành công</span>
                                                : <span className="text-danger">Lỗi</span>}
                                        </td>
                                        <td>{r.message}</td>
                                        <td>
                                            {r.id && (
                                                <a href={"/edit-hop-dong/" + r.id} target="_blank" rel="noreferrer">
                                                    Xem / Sửa
                                                </a>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                ) : (
                    /* BƯỚC 1: nhập thông tin */
                    <div className="row">
                        <div className="col-sm-6 col-12 form-group">
                            <label>Tên văn bản - hợp đồng (tiền tố) <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className="form-control height32"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                autoComplete="off"
                                placeholder="VD: Hợp đồng lao động 2026"
                            />
                            {title.trim() !== "" && (
                                <small className="text-muted">
                                    Tên mỗi hợp đồng = tiền tố + họ tên + mã nhân viên, VD:{" "}
                                    <strong>{title.trim()} Nguyễn Văn A MS000123</strong>
                                </small>
                            )}
                            {errors.title && <div className="text-danger"><small>{errors.title}</small></div>}
                        </div>

                        <div className="col-sm-6 col-12 form-group">
                            <label>Chọn mẫu văn bản - hợp đồng <span className="text-danger">*</span></label>
                            <SearchBoxModal
                                apiname={"hopdong_type"}
                                placeholder="Chọn văn bản/hợp đồng"
                                colvalue={"id"}
                                colname={"title"}
                                defaultValue={typeId}
                                name="type_id"
                                onChange={(e) => {
                                    setTypeId(e);
                                    if (e) loadMauHD(e);
                                }}
                            />
                            {errors.type_id && <div className="text-danger"><small>{errors.type_id}</small></div>}
                        </div>

                        <div className="col-sm-6 col-12 form-group">
                            <label>Chọn nhân sự <span className="text-danger">*</span></label>
                            <ModalSearchMulti
                                apiname={"users"}
                                placeholder="Chọn nhân sự"
                                colvalue={"id"}
                                colname={"fullname"}
                                defaultValue={userIds}
                                name="user_id"
                                multiple={true}
                                onChange={(e) => setUserIds(Array.isArray(e) ? e : [])}
                                onDataList={(list) => {
                                    // Lưu nhãn để bảng kết quả hiện tên thay vì id.
                                    setUserLabels((current) => {
                                        const next = Object.assign({}, current);
                                        list.forEach((item) => {
                                            if (item.value) next[item.value] = item.label;
                                        });
                                        return next;
                                    });
                                }}
                            />
                            {userIds.length > 0 && (
                                <small className="text-info">Đã chọn {userIds.length} nhân sự</small>
                            )}
                            {errors.user_id && <div className="text-danger"><small>{errors.user_id}</small></div>}
                        </div>

                        {detailMau && detailMau.urlfile && (
                            <div className="col-12 col-sm-6 form-group">
                                <label>Xem mẫu văn bản - hợp đồng</label>
                                <PreviewFile
                                    isEdit={false}
                                    detail={{
                                        list_file: (() => {
                                            try { return JSON.parse(detailMau.urlfile); }
                                            catch (e) { return [detailMau.urlfile]; }
                                        })(),
                                    }}
                                />
                            </div>
                        )}

                        {listMauParam.length > 0 && (
                            <div className="col-12">
                                {/* Mẫu có thể có tới 55 tham số => card thu gọn, mặc định đóng. */}
                                <div className="card card-filter mb-3">
                                    <div className="card-header" style={{ cursor: "pointer" }}>
                                        <div className="d-flex align-items-center" onClick={() => setShowMauParam(!showMauParam)}>
                                            <div className="mr-auto">
                                                <h3 className="title">
                                                    Tham số trong văn bản Mẫu
                                                    <span className="badge badge-secondary ml-2">{listMauParam.length}</span>
                                                    {customKeys.length > 0 && (
                                                        <span className="badge badge-warning ml-1">
                                                            {customKeys.length} tùy biến
                                                        </span>
                                                    )}
                                                </h3>
                                            </div>
                                            <button type="button" className="btn btn-sm btn-defaul">
                                                <span className={!showMauParam ? "fas fa-angle-down" : "fas fa-angle-up"}></span>
                                            </button>
                                        </div>
                                    </div>
                                    <div className={showMauParam ? "card-body" : "hidden-filter"} style={{ overflow: "hidden" }}>
                                        {showMauParam && (
                                            <>
                                                <small className="text-muted d-block mb-2">
                                                    <span className="badge badge-light border mr-1">Xám</span> hệ thống tự điền theo từng nhân sự
                                                    <span className="badge badge-warning ml-2 mr-1">Vàng</span> tùy biến, nhập bên dưới
                                                </small>
                                                <div className="d-flex flex-wrap" style={{ gap: 8, maxHeight: 220, overflowY: "auto" }}>
                                                    {listMauParam.map((k, i) => {
                                                        const isCustom = isCustomTemplateParam(k);
                                                        const label = k.label || k.value || k.title;
                                                        return (
                                                            <span
                                                                key={i}
                                                                className={"border rounded px-2 py-1 " + (isCustom ? "bg-warning bg-opacity-10 border-warning" : "bg-light")}
                                                                title={isCustom ? "Tùy biến nhập bên dưới" : "Hệ thống tự merge theo từng nhân sự"}
                                                            >
                                                                <span>{label}: </span>
                                                                <code>{toPlaceholder(k.title)}</code>
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {customKeys.length > 0 && userIds.length === 0 && (
                            <div className="col-12">
                                <div className="alert alert-warning py-1 px-2">
                                    <small>
                                        Mẫu này có <strong>{customKeys.length}</strong> tham số tùy biến cần nhập.
                                        Chọn nhân sự để nhập giá trị cho từng người.
                                    </small>
                                </div>
                            </div>
                        )}

                        {customKeys.length > 0 && userIds.length > 0 && (
                            <div className="col-12">
                                <div className="card mb-3">
                                    <div className="card-header">
                                        <div className="d-flex justify-content-between align-items-center">
                                        <h3 className="title mb-0">
                                            Giá trị tham số tùy biến theo từng nhân sự
                                            <span className="badge badge-info ml-2">{userIds.length} người</span>
                                        </h3>
                                        {userIds.length > 1 && (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={copyFirstToAll}
                                                title="Sao chép toàn bộ giá trị của người đầu tiên cho những người còn lại"
                                            >
                                                <span className="fas fa-copy"></span> Chép dòng đầu cho tất cả
                                            </button>
                                        )}
                                        </div>
                                    </div>
                                    <div className="card-body">

                                    {/* Điền nhanh: gõ 1 lần, áp cho mọi nhân sự */}
                                    {userIds.length > 1 && (
                                        <div className="border rounded p-2 mb-2 bg-light">
                                            <small className="text-muted d-block mb-2">
                                                Điền nhanh — gõ giá trị rồi bấm "Áp dụng" để điền cho tất cả nhân sự:
                                            </small>
                                            <div className="row">
                                                {customKeys.map((c) => (
                                                    <div className="col-12 col-sm-6 mb-2" key={c.key}>
                                                        <div className="input-group">
                                                            <input
                                                                type="text"
                                                                className="form-control height32"
                                                                placeholder={c.label || c.key}
                                                                value={fillAll[c.key] || ""}
                                                                onChange={(e) =>
                                                                    setFillAll({ ...fillAll, [c.key]: e.target.value })
                                                                }
                                                            />
                                                            <div className="input-group-prepend">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-success height32"
                                                                    onClick={() => applyFillAll(c.key)}
                                                                >
                                                                    Áp dụng
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Mỗi nhân sự 1 dòng, mỗi param 1 cột */}
                                    <div style={{ maxHeight: 340, overflowY: "auto" }}>
                                        <table className="table table-sm table-bordered mb-0">
                                            <thead className="thead-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                                <tr>
                                                    <th style={{ width: 40 }}>#</th>
                                                    <th style={{ minWidth: 160 }}>Nhân sự</th>
                                                    {customKeys.map((c) => (
                                                        <th key={c.key} style={{ minWidth: 150 }}>
                                                            {c.label || c.key}
                                                            <br />
                                                            <code style={{ fontSize: 10 }}>{c.key}</code>
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {userIds.map((uid, i) => (
                                                    <tr key={uid}>
                                                        <td>{i + 1}</td>
                                                        <td>{userLabels[uid] || uid}</td>
                                                        {customKeys.map((c) => (
                                                            <td key={c.key}>
                                                                <input
                                                                    type="text"
                                                                    className="form-control height32"
                                                                    placeholder="Dữ liệu"
                                                                    value={(paramsByUser[uid] || {})[c.key] || ""}
                                                                    onChange={(e) => editParam(uid, c.key, e.target.value)}
                                                                />
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <small className="text-muted">
                                        Ô để trống sẽ thay placeholder bằng rỗng trong file hợp đồng.
                                    </small>
                                    </div>
                                </div>
                            </div>
                        )}

                        {loading && (
                            <div className="col-12">
                                <div className="alert alert-info">
                                    Đang tạo hợp đồng {progress.current}/{progress.total}... Vui lòng không đóng cửa sổ.
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                {done ? (
                    <button type="button" className="btn btn-sm btn-success" onClick={handleClose}>
                        Xong
                    </button>
                ) : (
                    <>
                        <button type="button" className="btn btn-sm btn-secondary" onClick={handleClose} disabled={loading}>
                            Đóng
                        </button>
                        <button type="button" className="btn btn-sm btn-success" onClick={handleSubmit} disabled={loading}>
                            {loading ? <Spinner /> : <>Tạo {userIds.length > 0 ? userIds.length + " hợp đồng" : "hợp đồng"}</>}
                        </button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
}
